import {
    Backdrop,
    Box,
    Modal,
    Fade,
    Paper,
    Typography,
    IconButton,
    CircularProgress,
    // Divider,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone"
import useNotifications from "./useNotifications"
import useNotificationPrompt from "./useNotificationPrompt"
import { useEffect } from "react"

export default function NotificationModal({ open, onClose }) {
    const {
        notifications,
        isPending,
        isError,
        refetch,
        markAsRead,
        deleteNotification,
    } = useNotifications()
    const { promptAndSaveToken } = useNotificationPrompt()

    useEffect(() => {
        if (open) {
            refetch()
            promptAndSaveToken()
        }
    }, [open, refetch, promptAndSaveToken])

    const handleNotificationClick = (notification) => {
        if (!notification.read_status) {
            markAsRead({
                notification_object_id: notification.notification_object_id,
            })
        }
    }

    const handleDelete = (e, notification_object_id) => {
        e.stopPropagation()
        deleteNotification({ notification_object_id })
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{ backdrop: { timeout: 300 } }}
            sx={{ zIndex: 1500 }}
        >
            <Fade in={open}>
                <Paper
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90vw", sm: 450 },
                        maxHeight: "70vh",
                        borderRadius: "20px",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            px: 3,
                            pt: 2.5,
                            pb: 1.5,
                        }}
                    >
                        <Typography
                            variant="h6"
                            fontWeight={600}
                            color="text.primary"
                        >
                            Notifications
                        </Typography>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* <Divider /> */}

                    {/* Body */}
                    <Box sx={{ overflowY: "auto", px: 2, py: 1.5 }}>
                        {isPending && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    py: 4,
                                }}
                            >
                                <CircularProgress size={28} />
                            </Box>
                        )}

                        {isError && (
                            <Typography
                                sx={{ px: 2, py: 3, textAlign: "center" }}
                                color="text.secondary"
                            >
                                Failed to load notifications.
                            </Typography>
                        )}

                        {!isPending &&
                            !isError &&
                            notifications.length === 0 && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        py: 5,
                                        gap: 1,
                                        color: "text.disabled",
                                    }}
                                >
                                    <NotificationsNoneIcon
                                        sx={{ fontSize: 40 }}
                                    />
                                    <Typography variant="body2">
                                        No notifications yet.
                                    </Typography>
                                </Box>
                            )}

                        {!isPending && !isError && notifications.length > 0 && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                }}
                            >
                                {notifications.map((notification) => (
                                    <Box
                                        key={
                                            notification.notification_object_id
                                        }
                                        onClick={() =>
                                            handleNotificationClick(
                                                notification
                                            )
                                        }
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            justifyContent: "space-between",
                                            gap: 1,
                                            p: 1.5,
                                            border: 1,
                                            borderColor:
                                                notification.read_status
                                                    ? "divider"
                                                    : "primary.main",
                                            borderRadius: 1,
                                            cursor: "pointer",
                                            bgcolor: notification.read_status
                                                ? "transparent"
                                                : "action.hover",
                                            "&:hover": {
                                                bgcolor: "action.selected",
                                            },
                                        }}
                                    >
                                        {/* Unread dot */}
                                        <Box
                                            sx={{
                                                mt: 0.6,
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                flexShrink: 0,
                                                bgcolor:
                                                    notification.read_status
                                                        ? "transparent"
                                                        : "primary.main",
                                            }}
                                        />

                                        {/* Content */}
                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    alignItems: "baseline",
                                                    gap: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={
                                                        notification.read_status
                                                            ? 400
                                                            : 600
                                                    }
                                                    color="text.primary"
                                                    sx={{
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {notification.title ||
                                                        "Notification"}
                                                </Typography>
                                                {notification.created_on && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.disabled"
                                                        sx={{
                                                            whiteSpace:
                                                                "nowrap",
                                                            flexShrink: 0,
                                                            display: {
                                                                xs: "none",
                                                                sm: "block",
                                                            },
                                                        }}
                                                    >
                                                        {new Date(
                                                            notification.created_on
                                                        ).toLocaleDateString(
                                                            undefined,
                                                            {
                                                                month: "short",
                                                                day: "numeric",
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                            }
                                                        )}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {notification.body && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    component="p"
                                                    sx={{ mt: 0.25 }}
                                                >
                                                    {notification.body}
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Delete */}
                                        <IconButton
                                            size="small"
                                            onClick={(e) =>
                                                handleDelete(
                                                    e,
                                                    notification.notification_object_id
                                                )
                                            }
                                            sx={{
                                                flexShrink: 0,
                                                color: "text.disabled",
                                                "&:hover": {
                                                    color: "error.main",
                                                },
                                            }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Fade>
        </Modal>
    )
}
